import React, { useEffect, useState } from "react";
import { useSnackbar } from "@components";
import { Modal, Image, Segmented, ConfigProvider } from "antd";
import {
  FundViewOutlined,
  ProfileOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { workflowTruckById } from "../../../../../src/libs/apis";
import { WorkflowTruckI } from "../../../../../src/libs/interfaces";
import {
  COLOR_PRIMARY,
  COLOR_SECONDARY,
} from "../../../../../src/libs/constants";
import { Box, Grid, Typography } from "@mui/material";
import laImg from "../../../../images/no-photo.svg";

interface ShowContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

const PHOTO_KEYS = [
  "photo1",
  "photo2",
  "photo3",
  "photo4",
  "photo5",
  "photo6",
  "photo7",
  "photo8",
];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      color: COLOR_SECONDARY,
      pb: 0.75,
      mb: 1.5,
      borderBottom: "1px solid #e6ebf2",
    }}
  >
    {children}
  </Typography>
);

export const ShowContainer = ({ open, onClose, id }: ShowContainerPropsI) => {
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<WorkflowTruckI>({
    container: "",
  } as WorkflowTruckI);
  const [tab, setTab] = useState<string | number>("info");

  useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainer } = await workflowTruckById(id);
      if (!dataContainer) return;
      setData(dataContainer);
      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  const d = data as any;
  const photos = PHOTO_KEYS.map((k, i) => ({
    src: d[k] as string,
    alt: `Foto ${i + 1}`,
  })).filter((p) => p.src);

  const sections: { title: string; fields: [string, any][] }[] = [
    {
      title: "Datos generales",
      fields: [
        ["N° Contenedor", d.container],
        ["Exportador", d.client],
        ["MAGAP", d.magap],
        ["Productor", d.producer],
        ["Hacienda", d.farm],
        ["Zona", d.zone],
        ["Código empacadora", d.balerCode],
        ["Marca", d.brand],
        ["Tipo de caja", d.box],
        ["N° cajas", d.numCajas],
        ["Cajas por estiba", d.cajasEstiba],
      ],
    },
    {
      title: "Transporte",
      fields: [
        ["Conductor", d.driver],
        ["Identificación conductor", d.ciConductor],
        ["Placa", d.placa],
        ["Compañía de transporte", d.companyTransport],
      ],
    },
    {
      title: "Calidad - Gancho",
      fields: [
        ["Calidad", d.calidad],
        ["Identificación calidad", d.calidadCi],
        ["Gancho", d.gancho],
        ["Identificación gancho", d.ganchoCi],
      ],
    },
    {
      title: "Horas de proceso",
      fields: [
        ["Inicio de proceso", d.startProcess],
        ["Cierre de proceso", d.closeProcess],
        ["Fin de carga", d.endLoading],
        ["Cierre del contenedor", d.containerClose],
        ["Salida del contenedor", d.containerOulet],
      ],
    },
  ];

  return (
    <Modal
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "white" }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,.15)",
              fontSize: 18,
            }}
          >
            <FundViewOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
              Truck {data.container ? `N° ${data.container}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>
              {d.client || "Información y fotos del registro"}
            </Typography>
          </Box>
        </Box>
      }
      footer={null}
      loading={isLoading}
      open={open}
      onCancel={onClose}
      width={900}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      closeIcon={<span style={{ color: "white" }}>✕</span>}
      styles={{
        header: {
          background: COLOR_PRIMARY,
          margin: 0,
          padding: "16px 24px",
          borderRadius: "8px 8px 0 0",
        },
        content: { padding: 0, borderRadius: 8, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e6ebf2" }}>
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                itemSelectedBg: COLOR_PRIMARY,
                itemSelectedColor: "#fff",
                itemColor: COLOR_PRIMARY,
                trackBg: "#e6ebf2",
              },
            },
          }}
        >
          <Segmented
            block
            size="large"
            value={tab}
            onChange={setTab}
            options={[
              { value: "info", icon: <ProfileOutlined />, label: "Información" },
              { value: "photos", icon: <PictureOutlined />, label: `Fotos (${photos.length})` },
            ]}
          />
        </ConfigProvider>
      </Box>

      <Box sx={{ height: 520, overflowY: "auto", p: 2.5 }}>
        {tab === "info" && (
          <Grid container spacing={2.5}>
            {sections.map((sec) => (
              <Grid item xs={12} key={sec.title}>
                <SectionTitle>{sec.title}</SectionTitle>
                <Grid container spacing={2}>
                  {sec.fields.map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={`${sec.title}-${label}`}>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 500, wordBreak: "break-word" }}>
                        {value || "—"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <SectionTitle>Observación</SectionTitle>
              <Typography sx={{ fontSize: 14.5, whiteSpace: "pre-wrap" }}>
                {d.observation || "—"}
              </Typography>
            </Grid>
          </Grid>
        )}

        {tab === "photos" &&
          (photos.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "text.secondary", mt: 6 }}>
              Sin fotos registradas
            </Typography>
          ) : (
            <Image.PreviewGroup>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                }}
              >
                {photos.map((el) => (
                  <Box
                    key={el.alt}
                    sx={{
                      border: "1px solid #dde3ec",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
                    }}
                  >
                    <Box sx={{ bgcolor: COLOR_PRIMARY, color: "white", fontWeight: 700, fontSize: 13, px: 1.5, py: 0.75 }}>
                      {el.alt}
                    </Box>
                    <Image
                      alt={el.alt}
                      src={el.src}
                      fallback={laImg}
                      height={150}
                      width="100%"
                      style={{ objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Box>
            </Image.PreviewGroup>
          ))}
      </Box>
    </Modal>
  );
};
